import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Card, Col, Empty, Row, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  CHART_PALETTE,
  OTHERS_COLOR,
  MaterialSummaryRow,
  computeParetoAbc,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
} from "./materialShared";

// Two chart cards: "Cost Share" (donut: top 5 materials + aggregated Others,
// project total in the centre) and "Cost Concentration" (Pareto curve:
// cumulative % of total cost across cost-ranked materials, 80%/95% ABC
// cut-offs — answers "how few materials drive most of the spend").

const TOP_N = 5;

const truncate = (name: string, max = 22) =>
  name.length > max ? `${name.slice(0, max - 1)}…` : name;

const emptyBody = (
  <div
    style={{
      height: 300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Empty description="No material cost data" />
  </div>
);

const MaterialCostCharts: React.FC<{
  materials: MaterialSummaryRow[];
  grandTotal: number;
}> = ({ materials, grandTotal }) => {
  // Rows with a real cost only, in API order (already totalCost DESC). An
  // all-zero payload must not render fake slices/curves — it falls to Empty.
  const ranked = useMemo(
    () =>
      materials
        .map((m) => ({
          name: m.materialName || m.materialCode || `#${m.materialId}`,
          cost: num(m.totalCost),
          qty: num(m.totalQuantity),
          uom: m.uomName || "",
        }))
        .filter((m) => m.cost > 0),
    [materials]
  );

  const top = useMemo(() => ranked.slice(0, TOP_N), [ranked]);
  const hasData = ranked.length > 0;

  const othersCost = useMemo(() => {
    const topSum = top.reduce((sum, m) => sum + m.cost, 0);
    return Math.max(0, grandTotal - topSum);
  }, [top, grandTotal]);

  // Cumulative % of total cost at each rank + ABC class sizes
  // (A ≈ first 80% of spend, B → 95%, C = the long tail).
  const pareto = useMemo(() => computeParetoAbc(ranked), [ranked]);

  const donutOption = useMemo(() => {
    const slices: Array<{
      name: string;
      value: number;
      qty?: number;
      uom?: string;
      itemStyle: { color: string };
    }> = top.map((m, i) => ({
      name: m.name,
      value: m.cost,
      qty: m.qty,
      uom: m.uom,
      itemStyle: { color: CHART_PALETTE[i % CHART_PALETTE.length] },
    }));
    if (othersCost > 0) {
      slices.push({
        name: "Others",
        value: othersCost,
        itemStyle: { color: OTHERS_COLOR },
      });
    }
    return {
      animationDuration: 600,
      tooltip: {
        trigger: "item",
        // Keep the tooltip inside the chart box — without this it renders at
        // the cursor and gets clipped by the card / page edges.
        confine: true,
        extraCssText: "max-width: 320px; white-space: normal;",
        // Fallback so no default ECharts path ever prints a raw number
        valueFormatter: (value: any) => fmtINR(value),
        formatter: (params: any) => {
          const share = grandTotal > 0 ? (params.value / grandTotal) * 100 : 0;
          const slice = params?.data || {};
          const lines = [
            `<b>${params.name}</b>`,
            `Cost: ${fmtINRCompact(params.value)} (${fmtINR(params.value)})`,
            `Share: ${share.toFixed(1)}%`,
          ];
          if (slice.qty) {
            lines.splice(
              2,
              0,
              `Quantity: ${fmtQty(slice.qty)}${
                slice.uom ? ` ${slice.uom}` : ""
              }`
            );
          }
          return lines.join("<br/>");
        },
      },
      legend: {
        orient: "horizontal",
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { fontSize: 12, color: "#595959" },
        formatter: (name: string) => truncate(name, 18),
      },
      series: [
        {
          name: "Cost Share",
          type: "pie",
          radius: ["55%", "75%"],
          center: ["50%", "42%"],
          avoidLabelOverlap: true,
          // 2px surface gap between slices so identity never rests on hue alone
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          label: {
            show: true,
            position: "center",
            formatter: `${fmtINRCompact(grandTotal)}\nTotal`,
            fontSize: 16,
            fontWeight: 600,
            color: "#1a1a2e",
            lineHeight: 22,
          },
          labelLine: { show: false },
          data: slices,
        },
      ],
    };
  }, [top, othersCost, grandTotal]);

  const paretoOption = useMemo(
    () => ({
      animationDuration: 600,
      grid: { left: 8, right: 24, top: 24, bottom: 8, containLabel: true },
      tooltip: {
        trigger: "axis",
        confine: true,
        extraCssText: "max-width: 320px; white-space: normal;",
        // Fallback so no default ECharts path ever prints a raw number
        valueFormatter: (value: any) => `${num(value).toFixed(1)}%`,
        formatter: (paramsList: any) => {
          const p = pareto.points[paramsList?.[0]?.dataIndex];
          if (!p) return "";
          return [
            `<b>Top ${p.rank} material${p.rank === 1 ? "" : "s"}</b>`,
            `${truncate(p.name, 32)} — ${fmtINRCompact(p.cost)} (${fmtINR(
              p.cost
            )})`,
            `Cumulative: ${p.cumPct.toFixed(1)}% · ${fmtINRCompact(
              p.cumCost
            )} (${fmtINR(p.cumCost)})`,
          ].join("<br/>");
        },
      },
      xAxis: {
        type: "category",
        name: "Materials by cost rank",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: "#8c8c8c", fontSize: 11 },
        data: pareto.points.map((p) => p.rank),
        axisLine: { lineStyle: { color: "#d9d9d9" } },
        axisTick: { show: false },
        axisLabel: { color: "#8c8c8c" },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        max: 100,
        axisLabel: { color: "#8c8c8c", formatter: "{value}%" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          name: "Cumulative cost share",
          type: "line",
          data: pareto.points.map((p) => p.cumPct),
          lineStyle: { color: "#1677ff", width: 2 },
          itemStyle: { color: "#1677ff", borderColor: "#fff", borderWidth: 2 },
          symbol: "circle",
          symbolSize: pareto.points.length > 40 ? 4 : 8,
          areaStyle: { color: "rgba(22, 119, 255, 0.08)" },
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: { color: "#bfbfbf", type: "dashed" },
            label: {
              color: "#8c8c8c",
              fontSize: 11,
              formatter: "{c}%",
              position: "insideEndTop",
            },
            data: [{ yAxis: 80 }, { yAxis: 95 }],
          },
        },
      ],
    }),
    [pareto]
  );

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title="Cost Share"
          style={{ borderRadius: 12, height: "100%" }}
          headStyle={{ fontSize: 14 }}
          bodyStyle={{ padding: 12 }}
          extra={
            hasData ? (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                Top {top.length} material{top.length === 1 ? "" : "s"} + Others
              </span>
            ) : null
          }
        >
          {hasData ? (
            <ReactECharts
              option={donutOption}
              style={{ height: 300, width: "100%" }}
              notMerge={true}
              lazyUpdate={true}
            />
          ) : (
            emptyBody
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Cost Concentration (Pareto)
              <Tooltip
                overlayStyle={{ maxWidth: 340 }}
                title={
                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      How to read this graph
                    </div>
                    <div>
                      Materials are ranked left to right, costliest first. The
                      line shows the running share of total material cost — at
                      any point, "the top N materials together account for X% of
                      spend".
                    </div>
                    <div style={{ marginTop: 4 }}>
                      The steeper the start, the more your spend is concentrated
                      in a few materials. Where the line crosses the dashed 80%
                      mark is your Class A set — the few materials worth the
                      tightest procurement control. 95% marks Class B; the flat
                      tail after that is Class C.
                    </div>
                    <div style={{ marginTop: 4, color: "#d9d9d9" }}>
                      Hover any point for that material and the running total.
                    </div>
                  </div>
                }
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", fontSize: 13, cursor: "help" }}
                />
              </Tooltip>
            </span>
          }
          style={{ borderRadius: 12, height: "100%" }}
          headStyle={{ fontSize: 14 }}
          bodyStyle={{ padding: 12 }}
          extra={
            hasData && pareto.aCount > 0 ? (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                Top {pareto.aCount} of {pareto.points.length} ≈ 80% of{" "}
                {fmtINRCompact(grandTotal)}
              </span>
            ) : null
          }
        >
          {hasData ? (
            <>
              <ReactECharts
                option={paretoOption}
                style={{ height: 262, width: "100%" }}
                notMerge={true}
                lazyUpdate={true}
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                }}
              >
                <Tooltip title="Class A: materials making up the first ~80% of spend — tightest procurement control">
                  <Tag color="blue" style={{ marginRight: 0 }}>
                    A · {pareto.aCount} material{pareto.aCount === 1 ? "" : "s"}
                  </Tag>
                </Tooltip>
                <Tooltip title="Class B: next ~15% of spend (up to 95%)">
                  <Tag color="cyan" style={{ marginRight: 0 }}>
                    B · {pareto.bCount}
                  </Tag>
                </Tooltip>
                <Tooltip title="Class C: the long tail — last ~5% of spend">
                  <Tag style={{ marginRight: 0 }}>C · {pareto.cCount}</Tag>
                </Tooltip>
              </div>
            </>
          ) : (
            emptyBody
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default MaterialCostCharts;
